import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../lib/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  const [profileCompleted, setProfileCompleted] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      // SECURITY: Authentication now relies on httpOnly cookies set by the server
      // No tokens are stored in localStorage (XSS immune)
      const res = await axios.get(`${API}/auth/me`, { 
        withCredentials: true  // Send cookies with request
      });
      setUser(res.data);
      
      // Check profile completion status for logged-in users
      if (res.data) {
        try {
          const profileRes = await axios.get(`${API}/profile/status`, { 
            withCredentials: true 
          });
          setProfileCompleted(profileRes.data.profile_completed);
        } catch {
          setProfileCompleted(true); // Assume completed on error
        }
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = (userData, authToken, isNewRegistration = false) => {
    // SECURITY: Token is now stored in httpOnly cookie by the server
    // We only keep user data in memory state (not localStorage)
    setUser(userData);
    
    // Show profile completion modal for new registrations
    if (isNewRegistration) {
      setProfileCompleted(false);
      setShowProfileCompletion(true);
    }
  };

  const completeProfile = () => {
    setProfileCompleted(true);
    setShowProfileCompletion(false);
  };

  const triggerProfileCompletion = () => {
    setShowProfileCompletion(true);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setUser(null);
  };

  // SECURITY: No longer expose getAuthHeaders - authentication uses httpOnly cookies
  // This prevents XSS attacks from stealing tokens

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      logout, 
      checkAuth, 
      showProfileCompletion,
      setShowProfileCompletion,
      profileCompleted,
      completeProfile,
      triggerProfileCompletion
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
